// A message the SERVER produced, shown with the nature it declares — CCT-401.
//
// ⚠️ THE NATURE COMES FROM THE API, IT IS NOT GUESSED HERE. An error and a notice are not read
// the same way, and nothing in a sentence says which one it is: telling them apart client-side
// would mean matching on wording, which breaks at the first rewrite and in every language but
// one. So `messageType` travels with the message, and this component only renders it.
//
// ⚠️ AND THE ICON IS NOT THE ONLY SIGNAL. Colour alone excludes anybody who does not separate
// red from amber, which is why each nature also carries a DIFFERENT glyph: an "i" informs, a "!"
// warns, a cross refuses. The same rule the kanban columns already follow (CCT-345).
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { useTheme } from '../context/ThemeContext';
import { getThemeColors } from '../utils/themeStyles';

/** What the API declares. `helper` reads as information; anything unknown does too. */
export type ServerMessageType = 'info' | 'warning' | 'erreur' | 'helper';

type Palette = ReturnType<typeof getThemeColors>;

/** Icon and colour per nature. One table, so a fourth nature is added in one place. */
const APPARENCE: Record<string, (c: Palette) => { icone: string; teinte: string }> = {
  info: (c) => ({ icone: 'information-circle', teinte: c.info }),
  helper: (c) => ({ icone: 'information-circle', teinte: c.textTertiary }),
  warning: (c) => ({ icone: 'alert-circle', teinte: c.warning }),
  erreur: (c) => ({ icone: 'close-circle', teinte: c.error }),
};

export function apparenceDe(type: string | undefined, c: Palette) {
  return (APPARENCE[type ?? 'info'] ?? APPARENCE.info)(c);
}

export default function ServerMessage(
  { type, children, compact = false }:
  { type?: string; children: React.ReactNode; compact?: boolean },
) {
  const { isDarkTheme } = useTheme();
  const c = getThemeColors(isDarkTheme);
  const { icone, teinte } = apparenceDe(type, c);

  return (
    <View style={[s.rangee, compact ? s.compact : s.large]}>
      {/* ⚠️ `flexShrink: 0` — without it the circle is squeezed to an ellipse by a long sentence,
          because the text sibling grows first. Measured on the mission card. */}
      <Ionicons name={icone} size={compact ? 14 : 16} color={teinte} style={s.icone} />
      <Text style={[s.texte, { color: c.textSecondary, fontSize: compact ? 12 : 13 }]}>
        {children}
      </Text>
    </View>
  );
}

const s = StyleSheet.create({
  // Top-aligned, not centred: on two lines or more, an icon pinned to the middle of the block
  // stops reading as the marker of the first line.
  rangee: { flexDirection: 'row', alignItems: 'flex-start' },
  compact: { gap: 6 },
  large: { gap: 8, padding: 12 },
  icone: { flexShrink: 0, marginTop: 1 },
  texte: { flex: 1, lineHeight: 18 },
});
